import React, { useState, useEffect } from 'react';
import { ScrollView, View, Text, TouchableOpacity, Image, Platform } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import ProjectHeader from '../components/project/ProjectHeader';
import ProjectCard from '../components/project/ProjectCard';
import ProjectCardMobile from '../components/project/ProjectCardMobile';
import ProgressStages from '../components/project/ProgressStages';
import Pagination from '../components/common/Pagination';
import { HandleDownloadFunction, Project, RenderDescriptionWithAttachmentsFunction } from '../types/projectstatus';
import styles from '../styles/pages/projectstatusStyles';
import desktopStyles from '../styles/pages/projectstatusDesktopStyles';
import { API_URL } from 'src/config';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

export default function Projectstatus() {
  const [visibleProjects, setVisibleProjects] = useState<{
    [key: number]: boolean;
  }>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    fetchProjects();
  }, [currentPage, limit]);

  useEffect(() => {    // 페이지 변경 시 상세보기 상태 초기화
    setVisibleProjects({});
  }, [currentPage]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/redmine/projects?limit=${limit}&page=${currentPage}`,
      );
      const data = await response.json();
      const projectList = data?.data?.data || [];

      // 각 프로젝트의 ProjectTask done_ratio를 가져와서 업데이트
      const projectsWithTaskRatio = await Promise.all(
        projectList.map(async (project: Project) => {
          try {
            const progressResponse = await fetch(
              `${API_URL}/api/redmine/issues/percentageByProjectId/${project.id}`,
            );
            const progressData = await progressResponse.json();
            const projectTask = progressData?.data?.data?.find(
              (item: { subject: string }) => item?.subject === 'ProjectTask',
            );

            return {
              ...project,
              done_ratio: projectTask?.done_ratio !== undefined
                ? projectTask.done_ratio
                : project.done_ratio,
            };
          } catch {
            return project;
          }
        })
      );

      setProjects(projectsWithTaskRatio);
      setTotalCount(data?.data?.total_count || 0);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
      setTotalCount(0);
    }
  };

  const toggleProgress = async (projectId: number) => {
    const isCurrentlyVisible = visibleProjects[projectId];
    
    // 먼저 접기/펼치기 상태를 토글
    setVisibleProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));

    // 접는 경우에만 API 호출하지 않음
    if (isCurrentlyVisible) {
      return;
    }

    // 펼치는 경우에만 데이터 로드
    try {
      const progressResponse = await fetch(
        `${API_URL}/api/redmine/issues/percentageByProjectId/${projectId}`,
      );
      const progressData = await progressResponse.json();

      // 안전한 데이터 접근
      const projectTask = progressData?.data?.data
        ? progressData.data.data.find(
            (item: { subject: string }) => item?.subject === 'ProjectTask',
          )
        : null;

      const filteredData = projectTask
        ? projectTask.children
            ?.filter((child: { subject: string }) => {
              const validSubjects = ['Plan', 'Design', 'Publishing', 'Development', 'Issue', 'QA'];
              return validSubjects.includes(child?.subject);
            })
            ?.sort((a: { subject: string }, b: { subject: string }) => {
              // 정의된 순서대로 정렬
              const order = ['Plan', 'Design', 'Publishing', 'Development', 'Issue', 'QA'];
              return order.indexOf(a?.subject) - order.indexOf(b?.subject);
            }) || []
        : [];

      // ProjectTask의 done_ratio 가져오기
      const projectTaskDoneRatio = projectTask?.done_ratio;

      const newsResponse = await fetch(
        `${API_URL}/api/redmine/projects/${projectId}/latest-news`,
      );
      const newsData = await newsResponse.json();

      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === projectId
            ? {
                ...project,
                // ProjectTask의 done_ratio가 있으면 사용, 없으면 기존 값 유지
                done_ratio: projectTaskDoneRatio !== undefined ? projectTaskDoneRatio : project.done_ratio,
                progressData: filteredData,
                newsData: newsData?.data?.data || undefined, // 안전한 접근
              }
            : project,
        ),
      );
    } catch (error) {
      console.error('Error fetching project data for project', projectId, ':', error);
      // 에러 발생 시 빈 데이터로 설정
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === projectId
            ? {
                ...project,
                progressData: [],
                newsData: undefined,
              }
            : project,
        ),
      );
    }
  };

  const renderDescriptionWithAttachments: RenderDescriptionWithAttachmentsFunction = (description, attachments) => {
    const descriptionStyle = isDesktop ? desktopStyles.newsDescription : styles.newsDescription;
    const attachmentStyle = isDesktop ? desktopStyles.attachmentContainer : styles.attachmentContainer;
    const attachmentIconStyle = isDesktop ? desktopStyles.attachmentIcon : styles.attachmentIcon;
    const attachmentLinkStyle = isDesktop ? desktopStyles.attachmentLink : styles.attachmentLink;

    // description이 null, undefined, 또는 빈 문자열인 경우 처리
    if (!description) {
      return <Text style={descriptionStyle}>내용이 없습니다.</Text>;
    }

    // attachments가 없는 경우 description만 반환
    if (!attachments || !Array.isArray(attachments)) {
      return <Text style={descriptionStyle}>{description}</Text>;
    }

    // 설명을 줄 단위로 분할 (안전한 처리)
    const lines = String(description).split('\r\n');
    
    return lines.map((line, index) => {
      // 마크다운 이미지 참조 확인 (![](filename) 형식)
      const imgMatch = line.match(/!\[\]\(([^)]+)\)/);
      
      if (imgMatch) {
        const filename = imgMatch[1];
        // 해당 첨부파일 찾기 (키가 [](filename) 형태임)
        const markdownRefKey = `[](${filename})`;
        const attachment = attachments.find(att => 
          att && typeof att === 'object' && Object.keys(att)[0] === markdownRefKey
        );
        
        if (attachment) {
          const attachmentData = attachment[markdownRefKey];
          if (attachmentData && attachmentData.link && attachmentData.filename) {
            const downloadLink = `${API_URL}${attachmentData.link}`;
            
            return (
              <TouchableOpacity
                key={index}
                onPress={() => handleDownload(downloadLink, attachmentData.filename)}
                style={attachmentStyle}
              >
                <Text style={attachmentIconStyle}>📎</Text>
                <Text style={attachmentLinkStyle}>{attachmentData.filename}</Text>
              </TouchableOpacity>
            );
          }
        }
      }
      
      // 일반 텍스트 줄 반환
      return line.trim() ? (
        <Text key={index} style={descriptionStyle}>
          {line}
        </Text>
      ) : <View key={index} style={{height: 10}} />;  // 빈 줄은 간격으로 처리
    });
  };

  // 다운로드 처리 함수
  const handleDownload: HandleDownloadFunction = (url, filename) => {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    } else {
      console.log(`다운로드 중: ${url}`);
    }
  };

  // 날짜 포맷
  const formatDate = (dateString: string) => {
    return dateString.substring(0, 10);
  };

  const currentStyles = isDesktop ? desktopStyles : styles;

  return (
    <ScrollView style={currentStyles.container}>
      {!isDesktop && (
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <TouchableOpacity
              onPress={() => navigate('/')}
              style={styles.backButton}
            >
              <Text style={styles.backArrow}>←</Text>
            </TouchableOpacity>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                source={require('../assets/dainls.png')}
                style={{ width: 30, height: 30 }}
                resizeMode="contain"
              />
              <Text style={[styles.title, { marginLeft: 8 }]}>
                프로젝트 상태
              </Text>
            </View>
          </View>
        </View>
      )}

      <View style={isDesktop ? desktopStyles.contentWrapper : styles.contentWrapper}>
        {/* 필터 섹션 - PC와 모바일 동일한 디자인 */}
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 20 }}>
          <View style={[isDesktop ? desktopStyles.filterSection : styles.filterSection, { 
            width: 'auto',
            marginBottom: 0,
            paddingVertical: 8,
            paddingHorizontal: 16
          }]}>
            <Text style={{ fontSize: 16, fontWeight: '500', color: '#333', marginRight: 8 }}>
              표시 개수:
            </Text>
            <View style={[isDesktop ? desktopStyles.pickerWrapper : styles.pickerWrapper, { width: 80 }]}>
              <Picker
                selectedValue={limit}
                onValueChange={(itemValue) => {
                  setLimit(itemValue);
                  setVisibleProjects({}); // 표시개수 변경 시 상세보기 상태 초기화
                }}
                style={[isDesktop ? desktopStyles.picker : styles.picker, { width: 80 }]}
              >
                <Picker.Item label="5개" value={5} />
                <Picker.Item label="10개" value={10} />
                <Picker.Item label="25개" value={25} />
                <Picker.Item label="50개" value={50} />
                <Picker.Item label="100개" value={100} />
              </Picker>
            </View>
          </View>
        </View>

        {projects.map((project, index) => {          // PC 버전
          if (isDesktop) {
            return (
              <ProjectCard
                key={project.id}
                project={project}
                isExpanded={visibleProjects[project.id] || false}
                onToggle={() => toggleProgress(project.id)}
              >
                {/* 진행률 표시 */}
                {visibleProjects[project.id] && project.progressData && (
                  <View style={desktopStyles.progressSection}>
                    <Text style={desktopStyles.progressTitle}>프로젝트 진행 상황</Text>
                    <ProgressStages project={project} />
                  </View>
                )}

                {/* 뉴스피드 표시 */}
                {visibleProjects[project.id] && project.newsData && (
                  <View style={desktopStyles.newsSection}>
                    <Text style={desktopStyles.newsTitle}>최신 소식</Text>
                    <View style={desktopStyles.newsContent}>
                      <Text style={{ fontWeight: 'bold', marginBottom: 8, color: '#333' }}>
                        {project.newsData?.title || '제목 없음'}
                      </Text>
                      {renderDescriptionWithAttachments(
                        project.newsData?.description || '',
                        project.newsData?.attachments || [],
                      )}
                    </View>
                  </View>
                )}
              </ProjectCard>
            );
          }

          // 모바일 버전 - PC와 동일한 UI로 개선
          return (
            <ProjectCardMobile              key={project.id}
              project={project}
              isExpanded={visibleProjects[project.id] || false}
              onToggle={() => toggleProgress(project.id)}
            >
              {/* 진행률 표시 */}
              {visibleProjects[project.id] && project.progressData && (
                <>
                  <View style={styles.divider} />
                  <ProgressStages project={project} />
                </>
              )}

              {/* 뉴스피드 표시 */}
              {visibleProjects[project.id] && project.newsData && (
                <>
                  <View style={styles.divider} />
                  <View style={styles.newsContainer}>
                    <Text style={styles.newsTitle}>{project.newsData?.title || '제목 없음'}</Text>
                    <View style={styles.newsContent}>
                      {renderDescriptionWithAttachments(
                        project.newsData?.description || '',
                        project.newsData?.attachments || [],
                      )}
                    </View>
                  </View>
                </>
              )}
            </ProjectCardMobile>
          );
        })}

        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / limit)}
          onPageChange={setCurrentPage}
        />
      </View>
    </ScrollView>
  );
}